/**
 * Ad-hoc questionnaire section helpers
 * ────────────────────────────────────
 * The questionnaire's AI-captured ("ad-hoc") scope items are split into one top-level section per
 * coarse Business-Process group, plus a retained catch-all "Ad-hoc questions" section. The server is
 * the source of truth for that split (`splitAdhocSectionByGroup` in
 * apps/api/src/services/sales/deal-adhoc-fields.ts) - the constants and slug rule below MIRROR that
 * twin exactly so a client-side optimistic move produces the same `sectionKey`s the next server
 * reload would.
 *
 * `moveAdhocFieldToArea` lets the UI re-tag a scope item's functional area and reflect the move in the
 * questionnaire sidebar IMMEDIATELY (no reload), the same way the Prioritization pane already
 * re-clusters its flat list. Pure + type-driven, so it lives in shared and is unit-testable.
 */
import { normalizeBusinessProcessAlias } from './business-process.js';
/** Section key of the ad-hoc catch-all (ungrouped / user-created scope items). */
export const ADHOC_SECTION_KEY = 'ai_captured_questions';
/** Section-key prefix for a coarse Business-Process group section (peer of the catch-all). */
export const ADHOC_GROUP_SECTION_PREFIX = `${ADHOC_SECTION_KEY}__group__`;
/** True for the ad-hoc catch-all OR any coarse Business-Process group section. */
export function isAdhocSectionKey(sectionKey) {
    return (!!sectionKey &&
        (sectionKey === ADHOC_SECTION_KEY || sectionKey.startsWith(ADHOC_GROUP_SECTION_PREFIX)));
}
/**
 * True ONLY for a coarse Business-Process group section (a "functional area") - NOT the ungrouped
 * catch-all. These are the section headings the Scope doc lets the user rename + drag-reorder.
 */
export function isAdhocGroupSectionKey(sectionKey) {
    return !!sectionKey && sectionKey.startsWith(ADHOC_GROUP_SECTION_PREFIX);
}
/** Slugify a Business-Process name into the stable suffix used in a group section's key. Mirrors the
 *  server's `slugifyGroup` (lowercase, non-alnum → `_`, trim `_`, 50-char cap, `'group'` fallback). */
export function slugifyAdhocGroup(s) {
    return (s
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 50) || 'group');
}
/** The section key for a given Business-Process group (`''` maps to the catch-all). */
export function adhocGroupSectionKey(businessProcess) {
    const bp = businessProcess.trim();
    return bp === '' ? ADHOC_SECTION_KEY : `${ADHOC_GROUP_SECTION_PREFIX}${slugifyAdhocGroup(bp)}`;
}
/**
 * Reduce a functional-area section key to a canonical comparison token that is TOLERANT of the
 * key drift a business-process vocabulary change introduces. When the coarse Business-Process names
 * are recanonicalized (e.g. the NetSuite functional-area grouping folds "WMS" -> "Warehouse
 * Management"), a section's slug changes (`..._group__wms` -> `..._group__warehouse_management`),
 * so a diagram batch minted under the OLD name no longer matches the current section by exact key.
 *
 * The token strips the group prefix, un-slugs the suffix back to words, folds business-process
 * aliases to their canonical spelling (via `normalizeBusinessProcessAlias`), then re-slugs - so two
 * keys naming the same area under different (aliased) spellings reduce to the same token. Non-group
 * keys (the catch-all, template sections like `company-overview`) carry no business-process meaning
 * and are returned unchanged.
 */
export function canonicalAdhocAreaToken(sectionKey) {
    if (!sectionKey.startsWith(ADHOC_GROUP_SECTION_PREFIX))
        return sectionKey;
    const slug = sectionKey.slice(ADHOC_GROUP_SECTION_PREFIX.length);
    const name = normalizeBusinessProcessAlias(slug.replace(/_+/g, ' '));
    return `${ADHOC_GROUP_SECTION_PREFIX}${slugifyAdhocGroup(name)}`;
}
/**
 * True when two functional-area / section keys denote the SAME area: an exact match, OR the same
 * area named under different business-process aliases (see `canonicalAdhocAreaToken`). This is the
 * BPR diagram <-> questionnaire-section join, made resilient so a diagram batch generated before a
 * key rename still renders and refreshes against the current sections instead of silently
 * dropping out (`functionalAreaKey !== sectionKey`).
 */
export function functionalAreaKeyMatches(a, b) {
    if (!a || !b)
        return false;
    if (a === b)
        return true;
    return canonicalAdhocAreaToken(a) === canonicalAdhocAreaToken(b);
}
/** Set `businessProcess` on a scopeMeta object, preserving the rest (mirrors the in-place patch in
 *  `handleCommitAdhocScopeFields`). */
function withBusinessProcess(meta, bp) {
    return { ...(meta ?? {}), businessProcess: bp };
}
/**
 * Return a new questionnaire where the ad-hoc scope item identified by `fieldKey` has been moved into
 * the section for `targetBp` (its coarse Business-Process / functional area; `''` = the ungrouped
 * catch-all), and its group's primary field re-tagged with that `businessProcess`. Semantics mirror
 * the server split:
 *  - the group is appended to the destination section (created just before the catch-all if it doesn't
 *    yet exist, so the catch-all stays last);
 *  - the now-empty SOURCE group-section is dropped, but the catch-all is kept even when empty.
 *
 * Returns the questionnaire unchanged when `fieldKey` isn't found in an ad-hoc section, or the item is
 * already in the target section (no-op). Pure - never mutates its input.
 */
export function moveAdhocFieldToArea(questionnaire, fieldKey, targetBp) {
    const normalizedTarget = targetBp.trim();
    const destKey = adhocGroupSectionKey(normalizedTarget);
    // Locate the source ad-hoc section + group that holds fieldKey.
    let srcSectionKey = null;
    let movedGroup = null;
    for (const section of questionnaire.sections) {
        if (!isAdhocSectionKey(section.sectionKey))
            continue;
        const group = section.groups.find((g) => g.fields.some((f) => f.fieldKey === fieldKey));
        if (group) {
            srcSectionKey = section.sectionKey;
            movedGroup = group;
            break;
        }
    }
    // Not an ad-hoc field, or already in the destination section -> nothing to do.
    if (!movedGroup || srcSectionKey === null || srcSectionKey === destKey)
        return questionnaire;
    // Re-tag the group's primary field (the server's split keys off the primary's businessProcess; fall
    // back to the matched field when the group has no explicit primary) so both panes re-derive the
    // new area from scopeMeta.
    const hasPrimary = movedGroup.fields.some((f) => f.isAdhocPrimary);
    const retaggedGroup = {
        ...movedGroup,
        fields: movedGroup.fields.map((f) => f.isAdhocPrimary || (!hasPrimary && f.fieldKey === fieldKey)
            ? { ...f, scopeMeta: withBusinessProcess(f.scopeMeta, normalizedTarget) }
            : f),
    };
    let inserted = false;
    const sections = [];
    for (const section of questionnaire.sections) {
        if (section.sectionKey === srcSectionKey) {
            const remaining = section.groups.filter((g) => g !== movedGroup);
            // Drop an emptied group-section; keep the catch-all even when empty.
            if (remaining.length === 0 && section.sectionKey !== ADHOC_SECTION_KEY)
                continue;
            sections.push({ ...section, groups: remaining });
            continue;
        }
        if (section.sectionKey === destKey) {
            sections.push({ ...section, groups: [...section.groups, retaggedGroup] });
            inserted = true;
            continue;
        }
        sections.push(section);
    }
    if (!inserted) {
        // Destination is a brand-new group-section: insert it just before the catch-all (kept last).
        const catchAllIdx = sections.findIndex((s) => s.sectionKey === ADHOC_SECTION_KEY);
        const newSection = {
            sectionKey: destKey,
            label: destKey === ADHOC_SECTION_KEY ? 'Ad-hoc questions' : normalizedTarget,
            displayOrder: catchAllIdx >= 0 ? sections[catchAllIdx].displayOrder : sections.length,
            groups: [retaggedGroup],
        };
        if (catchAllIdx >= 0)
            sections.splice(catchAllIdx, 0, newSection);
        else
            sections.push(newSection);
    }
    return { ...questionnaire, sections };
}
//# sourceMappingURL=adhoc-sections.js.map